import React, { useEffect, useMemo, useRef, useState } from "react"
import FullCalendar from "@fullcalendar/react"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import esLocale from "@fullcalendar/core/locales/es"
import "../../../shared/styles/agenda-calendar.css"
import {
  DAY_KEYS,
  useScheduleAvailabilityConfig,
} from "../utils/scheduleAvailabilityConfig"

const AGENDA_RANGE_START_STORAGE_KEY = "odontoflow_agenda_range_start"
const AGENDA_RANGE_END_STORAGE_KEY = "odontoflow_agenda_range_end"
const AGENDA_VIEW_STORAGE_KEY = "odontoflow_agenda_calendar_view"

const SLOT_ROW_HEIGHT = 46
const EVENT_MIN_HEIGHT = 40

function getLocalDateKey(value) {
  if (!value) return ""

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ""

  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, "0")
  const dd = String(date.getDate()).padStart(2, "0")

  return `${yyyy}-${mm}-${dd}`
}

function buildLocalDateTime(dateKey, time) {
  const [hoursRaw, minutesRaw] = String(time || "00:00").split(":")
  const hours = Number(hoursRaw || 0)
  const minutes = Number(minutesRaw || 0)

  const date = new Date(`${dateKey}T00:00:00`)
  date.setHours(hours, minutes, 0, 0)
  return date
}

function addDays(date, days) {
  const value = new Date(date)
  value.setDate(value.getDate() + days)
  return value
}

function getFallbackVisibleRange(dateValue, calendarView) {
  const base = dateValue
    ? new Date(`${String(dateValue).slice(0, 10)}T12:00:00`)
    : new Date()

  if (Number.isNaN(base.getTime())) {
    const start = new Date()
    start.setHours(0, 0, 0, 0)
    return {
      start,
      end: addDays(start, 1),
    }
  }

  if (calendarView === "timeGridWeek") {
    const start = new Date(base)
    const day = start.getDay()
    const diffToMonday = day === 0 ? -6 : 1 - day

    start.setDate(start.getDate() + diffToMonday)
    start.setHours(0, 0, 0, 0)

    return {
      start,
      end: addDays(start, 7),
    }
  }

  const start = new Date(base)
  start.setHours(0, 0, 0, 0)

  return {
    start,
    end: addDays(start, 1),
  }
}

function normalizeBlockedDates(text) {
  return String(text || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => item.slice(0, 10))
}

function getDayConfig(scheduleConfig, date) {
  const dayKey = DAY_KEYS[date.getDay()]
  return scheduleConfig?.hours?.[dayKey] || null
}

function parseTimeToMinutes(time) {
  const [hoursRaw, minutesRaw] = String(time || "00:00").split(":")
  const hours = Number(hoursRaw || 0)
  const minutes = Number(minutesRaw || 0)

  return Math.max(0, Math.min(1440, hours * 60 + minutes))
}

function minutesToClock(minutes) {
  const safeMinutes = Math.max(0, Math.min(1440, Number(minutes || 0)))
  const hours = Math.floor(safeMinutes / 60)
  const mins = safeMinutes % 60

  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`
}

function minutesToCalendarTime(minutes) {
  return `${minutesToClock(minutes)}:00`
}

function floorToStep(minutes, step) {
  return Math.floor(minutes / step) * step
}

function ceilToStep(minutes, step) {
  return Math.ceil(minutes / step) * step
}

function getSafeSlotMinutes(scheduleConfig) {
  const configured = Number(scheduleConfig?.availability?.slotMinutes || 30)

  if ([15, 20, 30, 60].includes(configured)) {
    return configured
  }

  return 30
}

function getSlotDurationValue(slotMinutes) {
  if (slotMinutes >= 60) return "01:00:00"
  return `00:${String(slotMinutes).padStart(2, "0")}:00`
}

function formatCompactMeridiem(value) {
  const text = String(value || "").toLowerCase()
  if (text.includes("p")) return "p.m."
  return "a.m."
}

function getCompactSlotParts(date) {
  const value = date instanceof Date ? date : new Date(date)

  if (Number.isNaN(value.getTime())) {
    return { time: "", meridiem: "" }
  }

  const formatted = new Intl.DateTimeFormat("es-MX", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(value)

  const parts = formatted.split(/\s+/).filter(Boolean)
  const time = parts[0] || formatted
  const meridiem = formatCompactMeridiem(parts.slice(1).join(" "))

  return { time, meridiem }
}

function formatCompactWeekday(date) {
  const value = date instanceof Date ? date : new Date(date)

  if (Number.isNaN(value.getTime())) return ""

  const label = new Intl.DateTimeFormat("es-MX", {
    weekday: "short",
  }).format(value)

  return String(label || "").replace(".", "")
}

function getScheduleTimeWindow(scheduleConfig) {
  const slotMinutes = getSafeSlotMinutes(scheduleConfig)
  const paddingMinutes = slotMinutes

  const activeDays = Object.values(scheduleConfig?.hours || {}).filter(
    (day) => day?.enabled,
  )

  if (!activeDays.length) {
    return {
      slotMinutes,
      minMinutes: 480,
      maxMinutes: 1200,
      minTime: "08:00:00",
      maxTime: "20:00:00",
      gridStart: "08:00",
      gridEnd: "20:00",
    }
  }

  const starts = activeDays.map((day) => parseTimeToMinutes(day.start))
  const ends = activeDays.map((day) => parseTimeToMinutes(day.end))

  const earliest = Math.min(...starts)
  const latest = Math.max(...ends)

  let minMinutes = floorToStep(Math.max(0, earliest - paddingMinutes), slotMinutes)
  let maxMinutes = ceilToStep(Math.min(1440, latest + paddingMinutes), slotMinutes)

  if (maxMinutes <= minMinutes) {
    minMinutes = 480
    maxMinutes = 1200
  }

  if (maxMinutes - minMinutes < 120) {
    maxMinutes = Math.min(1440, minMinutes + 120)
  }

  return {
    slotMinutes,
    minMinutes,
    maxMinutes,
    minTime: minutesToCalendarTime(minMinutes),
    maxTime: minutesToCalendarTime(maxMinutes),
    gridStart: minutesToClock(minMinutes),
    gridEnd: minutesToClock(maxMinutes),
  }
}

function getSlotOperationalState(date, scheduleConfig) {
  const dateKey = getLocalDateKey(date)
  const dayConfig = getDayConfig(scheduleConfig, date)
  const blockedDates = normalizeBlockedDates(scheduleConfig?.breaks?.blockedDatesText)

  if (blockedDates.includes(dateKey)) {
    return {
      status: "blocked_date",
      allowed: false,
      reason: "Fecha bloqueada por configuración de agenda.",
    }
  }

  if (!dayConfig || !dayConfig.enabled) {
    return {
      status: "outside_schedule",
      allowed: false,
      reason: "Día no laborable según la configuración de agenda.",
    }
  }

  const workStart = buildLocalDateTime(dateKey, dayConfig.start)
  const workEnd = buildLocalDateTime(dateKey, dayConfig.end)

  if (date < workStart || date >= workEnd) {
    return {
      status: "outside_schedule",
      allowed: false,
      reason: "Horario fuera de la jornada configurada.",
    }
  }

  const breaks = scheduleConfig?.breaks || {}

  if (breaks.lunchEnabled) {
    const lunchStart = buildLocalDateTime(dateKey, breaks.lunchStart || "14:00")
    const lunchEnd = buildLocalDateTime(dateKey, breaks.lunchEnd || "15:00")

    if (date >= lunchStart && date < lunchEnd) {
      return {
        status: "break",
        allowed: false,
        reason: "Horario marcado como descanso o comida.",
      }
    }
  }

  return {
    status: "available",
    allowed: true,
    reason: "",
  }
}

function getBackgroundVisual(kind) {
  if (kind === "break") {
    return {
      className: "ofc-bg-break",
      backgroundColor: "rgba(187, 247, 208, 0.52)",
      borderColor: "rgba(16, 185, 129, 0.22)",
      label: "Descanso",
      labelColor: "#047857",
      dashedColor: "",
      hasDashedBorder: false,
    }
  }

  if (kind === "blocked") {
    return {
      className: "ofc-bg-blocked",
      backgroundColor: "rgba(254, 226, 226, 0.62)",
      borderColor: "rgba(248, 113, 113, 0.24)",
      label: "Bloqueado",
      labelColor: "#b91c1c",
      dashedColor: "rgba(185, 28, 28, 0.34)",
      hasDashedBorder: true,
    }
  }

  return {
    className: "ofc-bg-outside",
    backgroundColor: "rgba(148, 163, 184, 0.18)",
    borderColor: "rgba(100, 116, 139, 0.18)",
    label: "Fuera de horario",
    labelColor: "#64748b",
    dashedColor: "rgba(100, 116, 139, 0.44)",
    hasDashedBorder: true,
  }
}

function backgroundEvent(id, start, end, kind) {
  const visual = getBackgroundVisual(kind)

  return {
    id,
    start,
    end,
    display: "background",
    classNames: [visual.className],
    backgroundColor: visual.backgroundColor,
    borderColor: visual.borderColor,
    extendedProps: {
      backgroundKind: kind,
      backgroundLabel: visual.label,
    },
  }
}

function buildScheduleBackgroundEvents(scheduleConfig, visibleRange, timeWindow) {
  if (!visibleRange?.start || !visibleRange?.end) return []

  const rows = []
  const start = new Date(visibleRange.start)
  const end = new Date(visibleRange.end)

  let cursor = new Date(start)
  cursor.setHours(0, 0, 0, 0)

  while (cursor < end) {
    const dateKey = getLocalDateKey(cursor)
    const dayConfig = getDayConfig(scheduleConfig, cursor)
    const blockedDates = normalizeBlockedDates(scheduleConfig?.breaks?.blockedDatesText)

    const gridStart = buildLocalDateTime(dateKey, timeWindow.gridStart)
    const gridEnd = buildLocalDateTime(dateKey, timeWindow.gridEnd)

    if (blockedDates.includes(dateKey)) {
      rows.push(backgroundEvent(`blocked-${dateKey}`, gridStart, gridEnd, "blocked"))
      cursor = addDays(cursor, 1)
      continue
    }

    if (!dayConfig || !dayConfig.enabled) {
      rows.push(backgroundEvent(`outside-full-${dateKey}`, gridStart, gridEnd, "outside"))
      cursor = addDays(cursor, 1)
      continue
    }

    const workStart = buildLocalDateTime(dateKey, dayConfig.start)
    const workEnd = buildLocalDateTime(dateKey, dayConfig.end)

    if (gridStart < workStart) {
      rows.push(
        backgroundEvent(`outside-before-${dateKey}`, gridStart, workStart, "outside"),
      )
    }

    if (workEnd < gridEnd) {
      rows.push(backgroundEvent(`outside-after-${dateKey}`, workEnd, gridEnd, "outside"))
    }

    const breaks = scheduleConfig?.breaks || {}

    if (breaks.lunchEnabled) {
      const lunchStart = buildLocalDateTime(dateKey, breaks.lunchStart || "14:00")
      const lunchEnd = buildLocalDateTime(dateKey, breaks.lunchEnd || "15:00")

      if (lunchStart < lunchEnd && lunchEnd > gridStart && lunchStart < gridEnd) {
        rows.push(backgroundEvent(`break-${dateKey}`, lunchStart, lunchEnd, "break"))
      }
    }

    cursor = addDays(cursor, 1)
  }

  return rows
}

function getEventMarkerColor(event) {
  const ext = event.extendedProps || {}

  return (
    ext.customTagColor ||
    ext.markerColor ||
    ext.colorTag ||
    ext.tagColor ||
    ext.customColor ||
    ""
  )
}

function resolveFinancialStatus(event) {
  const ext = event.extendedProps || {}

  const explicitStatus = String(
    ext.financialStatus || event.financialStatus || "",
  ).toUpperCase()

  if (["PAID", "PARTIAL", "DEBT", "NONE"].includes(explicitStatus)) {
    return explicitStatus
  }

  const produced = Number(ext.producedAmount ?? event.producedAmount ?? 0)
  const paid = Number(ext.paidAmount ?? event.paidAmount ?? 0)
  const pending = Number(ext.pendingAmount ?? event.pendingAmount ?? 0)

  if (produced <= 0) return "NONE"
  if (pending <= 0 && paid > 0) return "PAID"
  if (paid > 0 && pending > 0) return "PARTIAL"
  if (pending > 0) return "DEBT"
  return "NONE"
}

function getBadgeStyle(tone) {
  if (tone === "success") {
    return {
      background: "#f0fdf4",
      color: "#166534",
      border: "1px solid #bbf7d0",
    }
  }

  if (tone === "info") {
    return {
      background: "#eff6ff",
      color: "#1d4ed8",
      border: "1px solid #bfdbfe",
    }
  }

  if (tone === "danger") {
    return {
      background: "#fef2f2",
      color: "#b91c1c",
      border: "1px solid #fecaca",
    }
  }

  if (tone === "warm") {
    return {
      background: "#fff7ed",
      color: "#c2410c",
      border: "1px solid #fed7aa",
    }
  }

  if (tone === "teal") {
    return {
      background: "#ecfeff",
      color: "#0f766e",
      border: "1px solid #a5f3fc",
    }
  }

  return {
    background: "#f8fafc",
    color: "#334155",
    border: "1px solid #cbd5e1",
  }
}

function getEventVisual(event) {
  const ext = event.extendedProps || {}

  const isHeld =
    String(event.id).startsWith("held-") ||
    ext.isHeld ||
    ext.status === "HELD"

  const isPlanLinked = Boolean(ext.isPlanLinked)
  const isCheckedIn = Boolean(ext.checkedIn)
  const isRecall =
    String(ext.source || "").toUpperCase().includes("RECALL") ||
    String(ext.reason || "").toUpperCase().includes("RECALL")

  const financialStatus = resolveFinancialStatus(event)

  let baseClass = "ofc-event ofc-event--normal"
  const badges = []

  if (isHeld && isPlanLinked) {
    baseClass = "ofc-event ofc-event--held-plan"
    badges.push({ label: "PLAN + RESERVA", tone: "warm" })
  } else if (isHeld) {
    baseClass = "ofc-event ofc-event--held"
    badges.push({ label: "RESERVA", tone: "warm" })
  } else if (isPlanLinked) {
    baseClass = "ofc-event ofc-event--plan"
    badges.push({ label: "PLAN", tone: "teal" })
  } else if (isRecall) {
    baseClass = "ofc-event ofc-event--recall"
    badges.push({ label: "RECALL", tone: "teal" })
  }

  if (isCheckedIn) {
    baseClass += " ofc-event--checked-overlay"
    badges.push({ label: "LLEGÓ", tone: "success" })
  }

  if (financialStatus === "PAID") {
    badges.push({ label: "PAGÓ", tone: "success" })
  } else if (financialStatus === "PARTIAL") {
    badges.push({ label: "DEBE", tone: "danger" })
  } else if (financialStatus === "DEBT") {
    badges.push({ label: "DEBE", tone: "danger" })
  }

  return {
    className: baseClass,
    badges,
  }
}

function inferBackgroundKind(info) {
  const classNames = info?.event?.classNames || []
  const id = String(info?.event?.id || "")

  if (
    info?.event?.extendedProps?.backgroundKind === "break" ||
    classNames.includes("ofc-bg-break") ||
    id.startsWith("break-")
  ) {
    return "break"
  }

  if (
    info?.event?.extendedProps?.backgroundKind === "blocked" ||
    classNames.includes("ofc-bg-blocked") ||
    id.startsWith("blocked-")
  ) {
    return "blocked"
  }

  if (
    info?.event?.extendedProps?.backgroundKind === "outside" ||
    classNames.includes("ofc-bg-outside") ||
    id.startsWith("outside-")
  ) {
    return "outside"
  }

  return ""
}

function paintBackgroundEvent(info, compact) {
  const kind = inferBackgroundKind(info)
  if (!kind) return

  const visual = getBackgroundVisual(kind)

  info.el.style.position = "absolute"
  info.el.style.inset = info.el.style.inset || "0"
  info.el.style.overflow = "hidden"
  info.el.style.opacity = "1"
  info.el.style.pointerEvents = "none"
  info.el.style.background = visual.backgroundColor
  info.el.style.border = `1px solid ${visual.borderColor}`
  info.el.style.boxShadow =
    kind === "break"
      ? "inset 0 0 0 1px rgba(16,185,129,0.12), inset 0 1px 0 rgba(255,255,255,0.56)"
      : "inset 0 0 0 1px rgba(100,116,139,0.08), inset 0 1px 0 rgba(255,255,255,0.45)"

  if (!info.el.querySelector(".ofc-bg-fill")) {
    const fill = document.createElement("div")
    fill.className = "ofc-bg-fill"
    fill.style.position = "absolute"
    fill.style.inset = "0"
    fill.style.pointerEvents = "none"
    fill.style.background =
      kind === "break"
        ? "linear-gradient(180deg, rgba(220,252,231,0.84), rgba(187,247,208,0.52))"
        : kind === "blocked"
          ? "linear-gradient(180deg, rgba(254,242,242,0.88), rgba(255,228,230,0.62))"
          : "linear-gradient(180deg, rgba(241,245,249,0.70), rgba(226,232,240,0.48))"

    info.el.appendChild(fill)
  }

  if (visual.hasDashedBorder && !info.el.querySelector(".ofc-bg-dashed")) {
    const dashed = document.createElement("div")
    dashed.className = "ofc-bg-dashed"
    dashed.style.position = "absolute"
    dashed.style.inset = compact ? "2px" : "3px"
    dashed.style.borderRadius = compact ? "6px" : "8px"
    dashed.style.pointerEvents = "none"
    dashed.style.border = `1px dashed ${visual.dashedColor}`

    info.el.appendChild(dashed)
  }

  if (!info.el.querySelector(".ofc-bg-label")) {
    const label = document.createElement("div")
    label.className = "ofc-bg-label"
    label.textContent = compact
      ? kind === "outside"
        ? "Fuera"
        : kind === "blocked"
          ? "Bloq."
          : "Desc."
      : visual.label

    label.style.position = "absolute"
    label.style.inset = "0"
    label.style.display = "flex"
    label.style.alignItems = "center"
    label.style.justifyContent = "center"
    label.style.textAlign = "center"
    label.style.padding = compact ? "0 6px" : "0 10px"
    label.style.background = "transparent"
    label.style.border = "0"
    label.style.boxShadow = "none"
    label.style.pointerEvents = "none"
    label.style.fontSize = compact ? "9px" : "11px"
    label.style.fontWeight = "850"
    label.style.letterSpacing = "0.01em"
    label.style.color = visual.labelColor

    info.el.appendChild(label)
  }
}

export default function AgendaCalendar({
  calendarRef,
  clinicId,
  date,
  events = [],
  calendarView,
  setCalendarView,
  handleDatesSet,
  handleEventDrop,
  handleEventResize,
  handleDateClick,
  handleEventClick,
  onBlockedSlotClick,
  compact = false,
}) {
  const [visibleRange, setVisibleRange] = useState(null)
  const lastDatesSetKeyRef = useRef("")

  const schedule = useScheduleAvailabilityConfig(clinicId)

  const timeWindow = useMemo(
    () => getScheduleTimeWindow(schedule.config),
    [schedule.config],
  )

  const slotDurationValue = useMemo(
    () => getSlotDurationValue(timeWindow.slotMinutes),
    [timeWindow.slotMinutes],
  )

  const effectiveVisibleRange = useMemo(
    () => visibleRange || getFallbackVisibleRange(date, calendarView),
    [visibleRange, date, calendarView],
  )

  const backgroundEvents = useMemo(
    () => buildScheduleBackgroundEvents(schedule.config, effectiveVisibleRange, timeWindow),
    [schedule.config, effectiveVisibleRange, timeWindow],
  )

  const calendarEvents = useMemo(
    () => [...backgroundEvents, ...(Array.isArray(events) ? events : [])],
    [backgroundEvents, events],
  )

  useEffect(() => {
    if (typeof window === "undefined") return
    if (!calendarView) return

    const api = calendarRef?.current?.getApi?.()
    if (!api) return

    if (api.view?.type !== calendarView) {
      api.changeView(calendarView)
    }
  }, [calendarRef, calendarView])

  const guardedDateClick = (arg) => {
    const slotState = getSlotOperationalState(arg.date, schedule.config)

    if (!slotState.allowed) {
      onBlockedSlotClick?.({
        date: arg.date,
        status: slotState.status,
        reason: slotState.reason,
        scheduleSource: schedule.source,
      })
      return
    }

    handleDateClick?.(arg)
  }

  const guardedEventClick = (info) => {
    if (info?.event?.display === "background") {
      info?.jsEvent?.preventDefault?.()
      info?.jsEvent?.stopPropagation?.()
      return
    }

    handleEventClick?.(info)
  }

  const eventRangeAllowed = (dropInfo) => {
    if (dropInfo?.allDay) return false

    const start = dropInfo?.start
    const end = dropInfo?.end || dropInfo?.start

    if (!start || !end) return true

    const cursor = new Date(start)
    const safeEnd = new Date(end)

    while (cursor < safeEnd) {
      const state = getSlotOperationalState(cursor, schedule.config)

      if (!state.allowed) {
        return false
      }

      cursor.setMinutes(cursor.getMinutes() + timeWindow.slotMinutes)
    }

    return true
  }

  const isWeekView = calendarView === "timeGridWeek"
  const shellClassName = [
    "ofc-shell",
    compact ? "ofc-shell--compact" : "",
    compact && isWeekView ? "ofc-shell--week" : "",
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <div
      className={shellClassName}
      style={{
        overflowX: "hidden",
        maxWidth: "100%",
      }}
    >
      <style>
        {`
          .ofc-shell {
            border-radius: 22px;
            background:
              radial-gradient(circle at top left, rgba(37, 99, 235, 0.055), transparent 34%),
              linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          }

          .ofc-shell .fc {
            --fc-border-color: #e5edf6;
            --fc-page-bg-color: transparent;
            --fc-neutral-bg-color: #f8fafc;
            --fc-today-bg-color: rgba(37, 99, 235, 0.075);
            color: #0f172a;
            font-family: inherit;
          }

          .ofc-shell .fc .fc-scrollgrid {
            border: 1px solid #e2e8f0 !important;
            border-radius: 18px !important;
            overflow: hidden !important;
            background: #ffffff !important;
            box-shadow: 0 18px 42px rgba(15, 23, 42, 0.055);
          }

          .ofc-shell .fc .fc-toolbar {
            gap: 10px;
            margin-bottom: 12px !important;
          }

          .ofc-shell .fc .fc-toolbar-title {
            font-size: 18px !important;
            font-weight: 950 !important;
            letter-spacing: -0.035em;
            color: #0f172a;
          }

          .ofc-shell .fc .fc-button {
            min-height: 36px !important;
            border-radius: 12px !important;
            border: 1px solid #dbe4f0 !important;
            background: #ffffff !important;
            color: #334155 !important;
            box-shadow: 0 8px 18px rgba(15, 23, 42, 0.045) !important;
            font-size: 12px !important;
            font-weight: 900 !important;
            text-transform: none !important;
            transition:
              transform 140ms ease,
              box-shadow 140ms ease,
              background 140ms ease,
              border-color 140ms ease !important;
          }

          .ofc-shell .fc .fc-button:hover {
            transform: translateY(-1px);
            background: #f8fbff !important;
            border-color: #bfdbfe !important;
            color: #1d4ed8 !important;
            box-shadow: 0 12px 24px rgba(37, 99, 235, 0.10) !important;
          }

          .ofc-shell .fc .fc-button-primary:not(:disabled).fc-button-active,
          .ofc-shell .fc .fc-button-primary:not(:disabled):active,
          .ofc-shell .fc .fc-button-group .fc-button.fc-button-active {
            background: linear-gradient(135deg, #2563eb, #4f46e5) !important;
            border-color: transparent !important;
            color: #ffffff !important;
            box-shadow: 0 14px 28px rgba(37, 99, 235, 0.20) !important;
          }

          .ofc-shell .fc .fc-col-header {
            background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          }

          .ofc-shell .fc .fc-col-header-cell {
            border-color: #e5edf6 !important;
          }

          .ofc-shell .fc .fc-col-header-cell-cushion {
            display: inline-flex;
            align-items: center;
            min-height: 38px;
            padding: 8px 6px !important;
            font-size: 12px;
            font-weight: 950 !important;
            color: #334155;
            text-transform: capitalize;
            cursor: pointer;
          }

          .ofc-shell .fc .fc-col-header-cell-cushion:hover {
            color: #1d4ed8;
          }

          .ofc-shell .fc .fc-day-today,
          .ofc-shell .fc .fc-timegrid-col.fc-day-today,
          .ofc-shell .fc .fc-timegrid-col-frame.fc-day-today,
          .ofc-shell .fc .fc-daygrid-day.fc-day-today {
            background: rgba(37, 99, 235, 0.08) !important;
          }

          .ofc-shell .fc .fc-timegrid-axis,
          .ofc-shell .fc .fc-timegrid-slot-label {
            background: #fbfdff;
          }

          .ofc-shell .fc .fc-timegrid-slot {
            height: ${SLOT_ROW_HEIGHT}px !important;
          }

          .ofc-shell .fc .fc-timegrid-slot-label {
            vertical-align: top !important;
          }

          .ofc-shell .fc .fc-timegrid-slot-label-cushion {
            color: #64748b !important;
            font-size: 12px !important;
            font-weight: 850 !important;
            letter-spacing: -0.01em;
            padding-top: 5px;
          }

          .ofc-shell .fc .fc-timegrid-slot-lane {
            background: rgba(255, 255, 255, 0.72);
            transition: background 120ms ease;
          }

          .ofc-shell .fc .fc-timegrid-slot-lane:hover {
            background: rgba(37, 99, 235, 0.035);
          }

          .ofc-shell .fc .fc-timegrid-col {
            background: linear-gradient(
              180deg,
              rgba(255, 255, 255, 0.88),
              rgba(248, 250, 252, 0.28)
            );
          }

          .ofc-shell .fc .fc-timegrid-col-frame {
            position: relative;
          }

          .ofc-shell .fc .fc-timegrid-col-bg {
            z-index: 1;
          }

          .ofc-shell .fc .fc-timegrid-col-events {
            z-index: 2;
          }

          .ofc-shell .fc .fc-bg-event {
            opacity: 1 !important;
            mix-blend-mode: normal !important;
          }

          .ofc-shell .fc .fc-bg-event.ofc-bg-outside,
          .ofc-shell .fc .fc-bg-event.ofc-bg-break,
          .ofc-shell .fc .fc-bg-event.ofc-bg-blocked {
            opacity: 1 !important;
            pointer-events: none !important;
            overflow: hidden !important;
            border-radius: 0 !important;
          }

          .ofc-shell .fc .fc-bg-event.ofc-bg-outside {
            background: rgba(148, 163, 184, 0.18) !important;
            border: 1px solid rgba(100, 116, 139, 0.18) !important;
          }

          .ofc-shell .fc .fc-bg-event.ofc-bg-break {
            background: linear-gradient(
              180deg,
              rgba(220, 252, 231, 0.84),
              rgba(187, 247, 208, 0.52)
            ) !important;
            border: 1px solid rgba(16, 185, 129, 0.22) !important;
          }

          .ofc-shell .fc .fc-bg-event.ofc-bg-blocked {
            background: linear-gradient(
              180deg,
              rgba(254, 242, 242, 0.88),
              rgba(255, 228, 230, 0.62)
            ) !important;
            border: 1px solid rgba(248, 113, 113, 0.24) !important;
          }

          .ofc-shell .fc .ofc-bg-fill,
          .ofc-shell .fc .ofc-bg-dashed,
          .ofc-shell .fc .ofc-bg-label {
            pointer-events: none;
          }

          .ofc-shell .fc .fc-timegrid-now-indicator-line {
            border-color: rgba(37, 99, 235, 0.86) !important;
            border-width: 2px !important;
            filter: drop-shadow(0 1px 4px rgba(37, 99, 235, 0.35));
            z-index: 5 !important;
          }

          .ofc-shell .fc .fc-timegrid-now-indicator-arrow {
            border-color: rgba(37, 99, 235, 0.86) !important;
            filter: drop-shadow(0 1px 4px rgba(37, 99, 235, 0.35));
            z-index: 5 !important;
          }

          .ofc-shell .fc .ofc-slot-half-hour {
            border-top-color: rgba(148, 163, 184, 0.45) !important;
          }

          .ofc-shell .fc .ofc-slot-quarter {
            border-top-color: rgba(226, 232, 240, 0.65) !important;
          }

          .ofc-shell .fc .fc-timegrid-event {
            min-height: ${EVENT_MIN_HEIGHT}px !important;
            border-radius: 10px !important;
            overflow: hidden !important;
          }

          .ofc-shell .fc .fc-timegrid-event-harness {
            margin-left: 2px !important;
            margin-right: 2px !important;
            filter: drop-shadow(0 10px 16px rgba(15, 23, 42, 0.08));
          }

          .ofc-shell .fc .fc-timegrid-event-harness .ofc-event {
            min-height: ${EVENT_MIN_HEIGHT}px;
            padding: 4px 7px 3px;
            display: grid;
            gap: 1px;
            align-content: center;
            border-radius: 13px !important;
            box-shadow:
              inset 0 1px 0 rgba(255, 255, 255, 0.56),
              0 8px 18px rgba(15, 23, 42, 0.08);
            transition:
              transform 130ms ease,
              box-shadow 130ms ease,
              filter 130ms ease;
          }

          .ofc-shell .fc .fc-timegrid-event-harness .ofc-event:hover {
            transform: translateY(-1px);
            box-shadow:
              inset 0 1px 0 rgba(255, 255, 255, 0.62),
              0 14px 28px rgba(15, 23, 42, 0.14);
            filter: saturate(1.03);
          }

          .ofc-shell .fc .ofc-event__top {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 6px;
            min-width: 0;
            line-height: 1;
          }

          .ofc-shell .fc .ofc-event__title {
            min-width: 0;
            font-size: 11px;
            line-height: 1.05;
            font-weight: 900;
            color: inherit !important;
            letter-spacing: -0.015em;
          }

          .ofc-shell .fc .ofc-event__badges {
            display: flex;
            align-items: center;
            gap: 3px;
            flex-wrap: nowrap !important;
            min-width: fit-content;
            margin-top: 1px !important;
          }

          .ofc-shell .fc .ofc-event__badges span {
            padding: 1px 5px !important;
            font-size: 8.8px !important;
            line-height: 1.05 !important;
          }

          .ofc-shell .fc .ofc-event__meta,
          .ofc-shell .fc .ofc-event__meta--primary {
            margin-top: 0 !important;
            font-size: 9.5px !important;
            line-height: 1.05 !important;
            font-weight: 800;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: rgba(51, 65, 85, 0.90) !important;
          }

          .ofc-shell .fc .fc-timegrid-event-harness .ofc-event--compact {
            min-height: ${EVENT_MIN_HEIGHT}px;
            display: flex;
            align-items: flex-start;
            padding: 4px 6px;
          }

          .ofc-shell .fc .fc-timegrid-event-harness .ofc-event--compact .ofc-event__title {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: 10.5px;
            line-height: 1.1;
          }

          .ofc-shell .fc .ofc-event--normal {
            background:
              linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(239,246,255,0.92) 100%) !important;
            color: #0f172a !important;
            border: 1px solid rgba(191, 219, 254, 0.92) !important;
            border-left: 4px solid #2563eb !important;
          }

          .ofc-shell .fc .ofc-event--held {
            background:
              linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(255,247,237,0.94) 100%) !important;
            color: #7c2d12 !important;
            border: 1px solid rgba(253, 186, 116, 0.95) !important;
            border-left: 4px solid #f97316 !important;
          }

          .ofc-shell .fc .ofc-event--held-plan {
            background:
              linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(245,243,255,0.92) 100%) !important;
            color: #4c1d95 !important;
            border: 1px solid rgba(196, 181, 253, 0.95) !important;
            border-left: 4px solid #7c3aed !important;
          }

          .ofc-shell .fc .ofc-event--plan {
            background:
              linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(236,253,245,0.92) 100%) !important;
            color: #064e3b !important;
            border: 1px solid rgba(167, 243, 208, 0.96) !important;
            border-left: 4px solid #14b8a6 !important;
          }

          .ofc-shell .fc .ofc-event--recall {
            background:
              linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(236,254,255,0.92) 100%) !important;
            color: #155e75 !important;
            border: 1px solid rgba(165, 243, 252, 0.95) !important;
            border-left: 4px solid #06b6d4 !important;
          }

          .ofc-shell .fc .ofc-event--checked-overlay {
            box-shadow:
              inset 0 0 0 1px rgba(22, 163, 74, 0.22),
              0 8px 18px rgba(15, 23, 42, 0.08) !important;
          }

          .ofc-shell .fc .ofc-day-header-button {
            background: transparent;
            border: none;
            padding: 0;
            margin: 0;
            font: inherit;
            color: inherit;
            cursor: pointer;
          }

          .ofc-shell .fc .ofc-day-header-button:hover {
            color: #1d4ed8;
          }

          .ofc-shell--compact .fc .fc-toolbar {
            display: grid !important;
            grid-template-columns: auto minmax(0, 1fr) auto;
            align-items: center !important;
            gap: 6px !important;
            margin-bottom: 8px !important;
          }

          .ofc-shell--compact .fc .fc-toolbar-chunk {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-wrap: nowrap;
            gap: 4px;
            min-width: 0;
          }

          .ofc-shell--compact .fc .fc-toolbar-chunk:first-child {
            justify-content: flex-start;
          }

          .ofc-shell--compact .fc .fc-toolbar-chunk:last-child {
            justify-content: flex-end;
          }

          .ofc-shell--compact .fc .fc-toolbar-title {
            font-size: 13px !important;
            line-height: 1.05 !important;
            text-align: center;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .ofc-shell--compact .fc .fc-button {
            min-height: 30px !important;
            padding: 4px 7px !important;
            font-size: 10.8px !important;
            border-radius: 10px !important;
          }

          .ofc-shell--compact .fc .fc-prev-button,
          .ofc-shell--compact .fc .fc-next-button {
            width: 31px !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }

          .ofc-shell--compact .fc .fc-timegrid-slot {
            height: 42px !important;
          }

          .ofc-shell--compact .fc .fc-timegrid-slot-label-cushion {
            font-size: 10.5px !important;
            line-height: 1.05 !important;
            padding-top: 4px !important;
          }

          .ofc-shell--compact .ofc-slot-label-compact {
            display: grid;
            justify-items: center;
            align-content: start;
            gap: 1px;
            min-width: 34px;
          }

          .ofc-shell--compact .ofc-slot-label-compact__time {
            font-size: 11px;
            font-weight: 850;
            line-height: 1;
            color: #475569;
          }

          .ofc-shell--compact .ofc-slot-label-compact__meridiem {
            font-size: 8.5px;
            font-weight: 750;
            line-height: 1;
            color: #94a3b8;
            letter-spacing: -0.01em;
          }

          .ofc-shell--compact .ofc-day-header-compact {
            display: grid;
            justify-items: center;
            gap: 1px;
            line-height: 1.05;
            min-height: 31px;
            align-content: center;
          }

          .ofc-shell--compact .ofc-day-header-compact__weekday {
            font-size: 10px;
            font-weight: 800;
            color: #475569;
            text-transform: capitalize;
          }

          .ofc-shell--compact .ofc-day-header-compact__day {
            font-size: 12px;
            font-weight: 850;
            color: #0f172a;
          }

          .ofc-shell--compact .fc .fc-col-header-cell-cushion {
            min-height: 32px;
            padding: 4px 2px !important;
            width: 100%;
            justify-content: center;
            font-weight: 850 !important;
          }

          .ofc-shell--compact .fc .ofc-bg-label {
            font-size: 8px !important;
            font-weight: 800 !important;
            line-height: 1.05 !important;
          }

          .ofc-shell--compact.ofc-shell--week .fc {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
          }

          .ofc-shell--compact.ofc-shell--week .fc .fc-scrollgrid,
          .ofc-shell--compact.ofc-shell--week .fc .fc-scrollgrid table {
            width: 100% !important;
            min-width: 0 !important;
          }

          .ofc-shell--compact.ofc-shell--week .fc .fc-timegrid-axis,
          .ofc-shell--compact.ofc-shell--week .fc .fc-timegrid-slot-label {
            width: 38px !important;
            min-width: 38px !important;
            max-width: 38px !important;
          }

          .ofc-shell--compact.ofc-shell--week .fc .fc-timegrid-slot-label-cushion {
            padding-left: 1px !important;
            padding-right: 1px !important;
          }

          .ofc-shell--compact.ofc-shell--week .fc .fc-timegrid-col-frame {
            min-width: 0 !important;
          }

          .ofc-shell--compact.ofc-shell--week .fc .fc-timegrid-event-harness {
            margin-left: 1px !important;
            margin-right: 1px !important;
          }

          .ofc-shell--compact.ofc-shell--week .fc .fc-timegrid-event-harness .ofc-event--compact {
            padding: 3px 3px !important;
            border-left-width: 3px !important;
          }

          .ofc-shell--compact.ofc-shell--week .fc .fc-timegrid-event-harness .ofc-event--compact .ofc-event__title {
            font-size: 9.5px !important;
            line-height: 1.08 !important;
          }

          .ofc-shell--compact.ofc-shell--week .fc .ofc-event__badges,
          .ofc-shell--compact.ofc-shell--week .fc .ofc-event__badges span {
            display: none !important;
          }

          .ofc-shell--compact:not(.ofc-shell--week) .fc .fc-timegrid-axis,
          .ofc-shell--compact:not(.ofc-shell--week) .fc .fc-timegrid-slot-label {
            width: 54px !important;
            min-width: 54px !important;
          }

        `}
      </style>

      <FullCalendar
        ref={calendarRef}
        plugins={[timeGridPlugin, interactionPlugin]}
        locales={[esLocale]}
        locale="es"
        initialView={calendarView || "timeGridDay"}
        initialDate={date}
        navLinks
        navLinkDayClick={(dateInfo) => {
          const api = calendarRef?.current?.getApi?.()
          if (!api) return

          api.changeView("timeGridDay", dateInfo)
          setCalendarView?.("timeGridDay")
        }}
        datesSet={(arg) => {
          const nextKey = [
            arg?.view?.type || "",
            arg?.startStr || "",
            arg?.endStr || "",
            timeWindow.minTime,
            timeWindow.maxTime,
          ].join("|")

          if (lastDatesSetKeyRef.current === nextKey) {
            return
          }

          lastDatesSetKeyRef.current = nextKey

          setVisibleRange((prev) => {
            const prevStart = prev?.start ? new Date(prev.start).getTime() : null
            const prevEnd = prev?.end ? new Date(prev.end).getTime() : null
            const nextStart = arg?.start ? new Date(arg.start).getTime() : null
            const nextEnd = arg?.end ? new Date(arg.end).getTime() : null

            if (prevStart === nextStart && prevEnd === nextEnd) {
              return prev
            }

            return {
              start: arg?.start || null,
              end: arg?.end || null,
            }
          })

          if (arg?.view?.type && arg.view.type !== calendarView) {
            setCalendarView?.(arg.view.type)
          }

          if (typeof window !== "undefined") {
            if (arg?.view?.type) {
              localStorage.setItem(
                AGENDA_VIEW_STORAGE_KEY,
                arg.view.type,
              )
            }

            localStorage.setItem(
              AGENDA_RANGE_START_STORAGE_KEY,
              String(arg?.startStr || "").slice(0, 10),
            )
            localStorage.setItem(
              AGENDA_RANGE_END_STORAGE_KEY,
              String(arg?.endStr || "").slice(0, 10),
            )
          }

          handleDatesSet?.(arg)
        }}
        editable
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        eventDurationEditable
        allDaySlot={false}
        slotEventOverlap={false}
        eventAllow={eventRangeAllowed}
        events={calendarEvents}
        slotMinTime={timeWindow.minTime}
        slotMaxTime={timeWindow.maxTime}
        height={compact ? "calc(100vh - 174px)" : "auto"}
        allDayText="Todo el día"
        nowIndicator
        eventMinHeight={EVENT_MIN_HEIGHT}
        eventShortHeight={EVENT_MIN_HEIGHT}
        buttonText={{
          today: "Hoy",
          day: "Día",
          week: "Semana",
          timeGridDay: "Día",
          timeGridWeek: "Semana",
        }}
        dayHeaderFormat={
          compact
            ? {
                weekday: "short",
                day: "numeric",
              }
            : {
                weekday: "long",
                day: "numeric",
                month: "long",
              }
        }
        dayHeaderContent={(arg) => {
          if (!compact) return arg.text

          const weekday = formatCompactWeekday(arg.date)
          const day = arg.date?.getDate?.() || ""

          return {
            html: `<span class="ofc-day-header-compact"><span class="ofc-day-header-compact__weekday">${weekday}</span><span class="ofc-day-header-compact__day">${day}</span></span>`
          }
        }}
        slotLabelFormat={{
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }}
        slotLabelContent={(arg) => {
          if (!compact) return arg.text

          const parts = getCompactSlotParts(arg.date)

          return {
            html: `<span class="ofc-slot-label-compact"><span class="ofc-slot-label-compact__time">${parts.time}</span><span class="ofc-slot-label-compact__meridiem">${parts.meridiem}</span></span>`
          }
        }}
        slotLabelInterval={slotDurationValue}
        slotDuration={slotDurationValue}
        snapDuration={slotDurationValue}
        dateClick={guardedDateClick}
        eventClick={guardedEventClick}
        headerToolbar={
          compact
            ? {
                left: "prev,next",
                center: "title",
                right: "today timeGridDay,timeGridWeek",
              }
            : {
                left: "prev,next today",
                center: "title",
                right: "timeGridDay,timeGridWeek",
              }
        }
        views={{
          timeGridDay: {
            buttonText: "Día",
          },
          timeGridWeek: {
            buttonText: "Semana",
            dayHeaderFormat: {
              weekday: "short",
              day: "numeric",
            },
          },
        }}
        slotLaneClassNames={(arg) => {
          const minutes = arg.date.getMinutes()
          if (minutes === 0 || minutes === 30) return ["ofc-slot-half-hour"]
          return ["ofc-slot-quarter"]
        }}
        eventDidMount={(info) => {
          if (info.event.display === "background") {
            paintBackgroundEvent(info, compact)
            return
          }

          info.el.style.background = "transparent"
          info.el.style.border = "none"
          info.el.style.boxShadow = "none"
        }}
        eventContent={(arg) => {
          if (arg.event.display === "background") return null

          const clinic = arg.event.extendedProps?.clinicName
          const doctor = arg.event.extendedProps?.doctorName
          const treatmentName =
            arg.event.extendedProps?.treatmentName ||
            arg.event.extendedProps?.reason

          const visual = getEventVisual(arg.event)
          const markerColor = getEventMarkerColor(arg.event)
          const isWeekView = arg.view?.type === "timeGridWeek"
          const primaryBadges = visual.badges.slice(0, 2)

          if (compact || isWeekView) {
            return (
              <div className={`${visual.className} ofc-event--compact`}>
                <div
                  style={{
                    display: "grid",
                    gap: 4,
                    minWidth: 0,
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      minWidth: 0,
                      width: "100%",
                    }}
                  >
                    {markerColor ? (
                      <span
                        title="Marca de color"
                        style={{
                          width: 7,
                          height: 7,
                          minWidth: 7,
                          borderRadius: "50%",
                          background: markerColor,
                          boxShadow: `0 0 0 2px ${markerColor}22`,
                        }}
                      />
                    ) : null}

                    <div
                      className="ofc-event__title"
                      style={{
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                        fontSize: 10.5,
                        fontWeight: 900,
                        lineHeight: 1.1,
                      }}
                    >
                      {arg.event.title}
                    </div>
                  </div>

                  {!!primaryBadges.length && (
                    <div
                      style={{
                        display: "flex",
                        gap: 3,
                        flexWrap: "wrap",
                      }}
                    >
                      {primaryBadges.map((badge) => (
                        <span
                          key={badge.label}
                          style={{
                            ...getBadgeStyle(badge.tone),
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "fit-content",
                            padding: "1px 5px",
                            borderRadius: 999,
                            fontSize: 9,
                            fontWeight: 900,
                            lineHeight: 1.15,
                          }}
                        >
                          {badge.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )
          }

          const secondaryLine = treatmentName || doctor || clinic || ""

          return (
            <div className={visual.className}>
              <div className="ofc-event__top">
                <div
                  className="ofc-event__title"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    minWidth: 0,
                  }}
                >
                  {markerColor ? (
                    <span
                      title="Marca de color"
                      style={{
                        width: 8,
                        height: 8,
                        minWidth: 8,
                        borderRadius: "50%",
                        background: markerColor,
                        boxShadow: `0 0 0 2px ${markerColor}22`,
                      }}
                    />
                  ) : null}

                  <span
                    style={{
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {arg.event.title}
                  </span>
                </div>

                {!!visual.badges.length && (
                  <div
                    className="ofc-event__badges"
                    style={{
                      display: "flex",
                      gap: 4,
                      flexWrap: "wrap",
                    }}
                  >
                    {visual.badges.map((badge) => (
                      <span
                        key={badge.label}
                        style={{
                          ...getBadgeStyle(badge.tone),
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "2px 7px",
                          borderRadius: 999,
                          fontSize: 9.5,
                          fontWeight: 900,
                          lineHeight: 1.15,
                        }}
                      >
                        {badge.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {secondaryLine ? (
                <div className="ofc-event__meta ofc-event__meta--primary">
                  {secondaryLine}
                </div>
              ) : null}
            </div>
          )
        }}
      />
    </div>
  )
}